import { ALL_INJECTION_PROTOCOLS } from "@src/packages/injection-protocol/protocols";
import { coachNameFromPrescribedName } from "@src/packages/injection-protocol/brand";
import type {
  InjectionProtocol,
  ProtocolIndication,
} from "@src/packages/injection-protocol/types";

import {
  methodIsOffered,
  methodsFor,
  protocolsForMethod,
} from "@src/packages/injection-coach/deviceChoices";

/**
 * Whether the injection coach belongs on screen, and which procedure it would
 * walk.
 *
 * Kept pure and out of the panel because `CycleDetailScreen` reaches
 * `react-native` and cannot be loaded by the test runner — the same reason
 * `resolveCycleDetailAction` was extracted in #2828. The decision that matters
 * here is "may a patient be offered guidance for a needle right now", and that is
 * not a decision to leave untestable.
 *
 * Deliberately narrow. #2267 pilots one medication, one route, one moment, so
 * anything this cannot positively identify returns null and the section does not
 * appear. Silence is the safe failure: a patient who sees nothing opens the
 * medication sheet she already had, while a patient shown the wrong procedure is
 * holding the wrong instructions.
 */

/** Routes the coach can guide. Oral and vaginal doses have no needle to coach. */
const INJECTABLE_INTAKE_TYPES = new Set([
  "subcutaneous",
  "intramuscular",
  "injection",
]);

/**
 * The step the trigger shot belongs to (`cycleHeaderSteps.ts`). Ovidrel *is* the
 * trigger, so pinning the coach to this step means it cannot surface at a moment
 * the protocol does not describe.
 */
export const INJECTION_COACH_STEP_ID = "trigger_shot";

/**
 * Why the route is hers and not her record's.
 *
 * It looked like the discriminator Ovidrel never needed: urinary hCG is one
 * vial and two procedures, and a dose recording one of them seemed to settle
 * it. It does not, because the field cannot say where its value came from.
 * Adding a medication by name in this app fills the route from a preset —
 * `AddMedicationModal.tsx` maps `pregnyl`, `novarel` and `hcg` to
 * `intramuscular` — so a dose can read "intramuscular" because a clinic said
 * so, or because a table did.
 *
 * Those are not the same fact, and the difference between them is a 38 mm
 * needle into the upper outer quadrant of her buttock or a short one into her
 * abdomen. This module's own rule decides it: never a default. Asking costs one
 * tap; guessing costs the wrong site.
 *
 * So both surfaces ask, and `confirmedRoute` — her answer — is the only thing
 * that picks between two procedures.
 */
export type InjectionCoachCandidate = {
  protocol: InjectionProtocol;
  /** The scheduled dose this session is for, so it can be logged afterwards. */
  medicationId: string;
  /**
   * The trade name to speak, taken from her prescription rather than from her
   * locale — Ovidrel and Ovitrelle are the same molecule dispensed under
   * different names in different markets, and which one she is holding has
   * nothing to do with which language she reads. See
   * `injection-protocol/brand.ts`.
   */
  brandName: string;
};

export type EligibilityInput = {
  /** The hub step currently on screen. */
  stepId: string | null;
  hasActiveCycle: boolean;
  /** Doses scheduled for today, from the calendar. */
  scheduledDoses: {
    medicationId: string;
    name: string;
    /** `intakeType` as the calendar carries it (`care/calendar.types.ts`). */
    intakeType?: string | null;
    /** `medication.form`, raw from the API — not the icon-mapped UI form. */
    apiForm?: string | null;
  }[];
  /**
   * The device the patient told us she has, used only when her prescription does
   * not say. See `deviceFromPrescription`.
   */
  confirmedForm: string | null;
  /**
   * The route she told us, used only when her prescription does not say.
   *
   * Asked rather than assumed, and asked rather than skipped: refusing to coach
   * a dose whose route is unrecorded would withdraw guidance from the hardest
   * injection in the catalogue, and the answer is on the prescription she is
   * holding. A default would be worse than either — it would be instructions
   * for a site she was not told to use.
   */
  confirmedRoute?: "intramuscular" | "subcutaneous" | null;
};

/**
 * Every procedure that could be the one she is holding, by name alone.
 *
 * Read from the registry rather than from a list of aliases here, which is what
 * makes the next medication a protocol file and not an edit to this one. The
 * match is a substring of her prescription's own name, because a prescription
 * says "Ovitrelle 250 mcg/0.5 mL" and a brand is "Ovitrelle".
 */
function protocolsForPrescribedName(name: string): InjectionProtocol[] {
  // Trade name or molecule name, whichever her record carries — the same
  // recognition the medication row uses, so the two surfaces cannot disagree
  // about whether a dose is coachable.
  return ALL_INJECTION_PROTOCOLS.filter(
    (protocol) =>
      protocol.indication === INDICATION_FOR_STEP &&
      coachNameFromPrescribedName(protocol, name) !== null,
  );
}

/**
 * What kind of injection belongs to the step this panel is pinned to.
 *
 * The pin has always been there — the coach appears on the trigger-shot step
 * and nowhere else — and it was doing nothing while every protocol in the
 * catalogue was a trigger. The antagonists broke that silently: `findCoachableDose`
 * takes the *first* dose in the day's list that any protocol recognises, and a
 * daily antagonist is active for eight to ten days before a trigger exists. So
 * a patient mid-stimulation was offered a full guided injection on the trigger
 * step, and finishing it recorded her antagonist under "that's your trigger
 * shot recorded".
 *
 * The step and the protocol now have to agree about what this moment is for.
 * When the coach gains a surface for daily injections, that surface names its
 * own indication and this constant stops being the only one.
 */
const INDICATION_FOR_STEP: ProtocolIndication = "trigger";

/**
 * The dose the coach would guide, before the device is known.
 *
 * Split from `resolveInjectionCoach` so the panel can tell "no injection here"
 * apart from "an injection, but I do not know the device yet" — the first renders
 * nothing, the second is what makes asking her worthwhile.
 */
export function findCoachableDose(
  input: Omit<EligibilityInput, "confirmedForm">,
): {
  medicationId: string;
  prescribedForm: string | null;
  /** The name as her prescription carries it, which is where the brand lives. */
  prescribedName: string;
} | null {
  if (!input.hasActiveCycle) return null;
  if (input.stepId !== INJECTION_COACH_STEP_ID) return null;

  const dose = input.scheduledDoses.find((candidate) => {
    const intake = candidate.intakeType?.trim().toLowerCase();
    if (!intake || !INJECTABLE_INTAKE_TYPES.has(intake)) return false;
    return protocolsForPrescribedName(candidate.name).length > 0;
  });

  if (!dose) return null;
  return {
    medicationId: dose.medicationId,
    prescribedForm: dose.apiForm?.trim().toLowerCase() ?? null,
    prescribedName: dose.name,
  };
}

export function resolveInjectionCoach(
  input: EligibilityInput,
): InjectionCoachCandidate | null {
  const dose = findCoachableDose(input);
  if (!dose) return null;

  const candidates = protocolsForPrescribedName(dose.prescribedName);
  if (candidates.length === 0) return null;

  // Her prescription first; what she told us only when it does not say. Never a
  // default — a protocol chosen by fallback would be instructions for a device
  // she may not be holding, or a site she was not told to use.
  //
  // Each narrowing is skipped when it cannot discriminate: Ovidrel's two
  // protocols share a route and differ by device, urinary hCG's share a device
  // and differ by route, and asking about the axis that is already settled
  // would be a question with one answer.
  // The administration method, in her calendar's own words — the same five
  // values her add-medication form writes, matched against what each procedure
  // declares it is dispensed as. This used to compare a translated device
  // concept, which put "poudre + solvant" and "flacon + seringue" on the same
  // side of the comparison. See `prescriptionForms`.
  const method = answerForMethod(
    candidates,
    dose.prescribedForm,
    input.confirmedForm,
  );
  const byForm: readonly InjectionProtocol[] | null = method
    ? protocolsForMethod(candidates, method)
    : methodsFor(candidates).length > 1
      ? null
      : candidates;
  if (!byForm) return null;

  // Hers, and only hers — see the note above `EligibilityInput`.
  const byRoute = narrow(
    [...byForm],
    (protocol) => protocol.route,
    input.confirmedRoute,
  );
  if (!byRoute) return null;

  // More than one left means an axis nobody has answered yet. The panel asks;
  // this stays silent rather than choosing.
  if (byRoute.length !== 1) return null;
  const protocol = byRoute[0]!;

  // Her prescription names the product; nothing else is allowed to. A dose that
  // reached here carries one of these names by definition, so a null means the
  // registry and the brand matcher have drifted apart — in which case the coach
  // stays silent rather than naming a product it cannot source from what she
  // was actually prescribed.
  const name = coachNameFromPrescribedName(protocol, dose.prescribedName);
  if (!name) return null;

  return { protocol, medicationId: dose.medicationId, brandName: name };
}

/**
 * Narrow by one axis, or leave the set alone where the axis cannot decide.
 *
 * Returns null only when an answer exists and excludes everything, which means
 * we have misidentified the medication rather than merely failed to ask.
 */
function narrow<T extends string>(
  protocols: InjectionProtocol[],
  axis: (protocol: InjectionProtocol) => T,
  answer: T | null | undefined,
): InjectionProtocol[] | null {
  if (protocols.length <= 1) return protocols;
  if (new Set(protocols.map(axis)).size === 1) return protocols;
  if (!answer) return protocols;
  const matching = protocols.filter((protocol) => axis(protocol) === answer);
  return matching.length > 0 ? matching : null;
}

/**
 * What the panel still has to ask before it can guide her.
 *
 * Derived from the same registry rather than hardcoded per medication, so a
 * third protocol that differs by a third axis is a question this already knows
 * how to describe.
 */
/**
 * The devices the candidate procedures come in, for the question that asks.
 *
 * Beside `unansweredAxis` because it answers the other half of the same
 * question: that one says *whether* to ask about the device, this says which
 * buttons the asking should have. They were one hardcoded pair — a pen and a
 * prefilled syringe — and follitropin alfa is a pen or a vial, so a patient was
 * offered a device she does not own and the button did nothing.
 */
/**
 * The procedures this dose could be, before any narrowing.
 *
 * Exported so the panel can hand them to `deviceDecision`, which is the
 * function the coach sheet asks the same question with. The panel used to ask
 * its own, shorter one.
 */
export function protocolsForEligibility(
  input: EligibilityInput,
): readonly InjectionProtocol[] {
  const dose = findCoachableDose(input);
  return dose ? protocolsForPrescribedName(dose.prescribedName) : [];
}

/** The administration method her calendar records for this dose, if any. */
export function recordedFormFor(input: EligibilityInput): string | null {
  return findCoachableDose(input)?.prescribedForm ?? null;
}

/**
 * The administration method to narrow by, prescription first, then her answer.
 *
 * Her record wins wherever it names something the catalogue actually has. Where
 * it names something the catalogue does not, it is ignored in favour of her own
 * answer — and where she has not answered yet, that leaves the axis open so she
 * is asked.
 *
 * That last case is not hypothetical. An Ovidrel dose recorded as
 * `lyophilized_powder` names a powder, Ovidrel ships none, and treating the
 * record as final made the whole coach disappear from a step where it used to
 * ask one question and then guide her. A prescription that contradicts the
 * catalogue has a mistake in it somewhere; she is holding the box, so she is
 * the better source. What is never done is *guessing* — an unanswerable axis
 * stays unanswered and the surface asks.
 */
function answerForMethod(
  candidates: InjectionProtocol[],
  prescribed: string | null,
  confirmed: string | null,
): string | null {
  if (prescribed && methodIsOffered(candidates, prescribed)) return prescribed;
  if (confirmed && methodIsOffered(candidates, confirmed)) return confirmed;
  return null;
}

export function unansweredAxis(
  input: EligibilityInput,
): "device" | "route" | null {
  const dose = findCoachableDose(input);
  if (!dose) return null;

  const candidates = protocolsForPrescribedName(dose.prescribedName);
  if (candidates.length <= 1) return null;

  const method = answerForMethod(
    candidates,
    dose.prescribedForm,
    input.confirmedForm,
  );
  if (!method && methodsFor(candidates).length > 1) return "device";

  const byForm: readonly InjectionProtocol[] = method
    ? protocolsForMethod(candidates, method)
    : candidates;
  if (!input.confirmedRoute && new Set(byForm.map((p) => p.route)).size > 1) {
    return "route";
  }

  return null;
}
